/**
 * React hooks for events
 * Integrates with TanStack Query for caching and state management
 */

import { useState, useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import * as eventsService from '../services/events';
import type { EventUpdate } from '../types/database';
import type { RecurrenceType } from '../types/events.types';

// Query keys for consistent cache management
export const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  list: (filters: string) => [...eventKeys.lists(), { filters }] as const,
  details: () => [...eventKeys.all, 'detail'] as const,
  detail: (id: number) => [...eventKeys.details(), id] as const,
  upcoming: () => [...eventKeys.all, 'upcoming'] as const,
  past: () => [...eventKeys.all, 'past'] as const,
  going: (userId: string) => [...eventKeys.all, 'going', userId] as const,
  search: (query: string) => [...eventKeys.all, 'search', query] as const,
  upcomingInfinite: () => [...eventKeys.all, 'upcoming', 'infinite'] as const,
  pastInfinite: () => [...eventKeys.all, 'past', 'infinite'] as const,
  goingInfinite: (userId: string) =>
    [...eventKeys.all, 'going', userId, 'infinite'] as const,
  series: (id: string) => [...eventKeys.all, 'series', id] as const,
  export: (options?: { dateFrom?: string; dateTo?: string }) =>
    [...eventKeys.all, 'export', options] as const,
};

/**
 * Fetch all events
 */
export function useEvents() {
  return useQuery({
    queryKey: eventKeys.lists(),
    queryFn: eventsService.getEvents,
  });
}

/**
 * Fetch upcoming events
 */
export function useUpcomingEvents() {
  return useQuery({
    queryKey: eventKeys.upcoming(),
    queryFn: eventsService.getUpcomingEvents,
  });
}

/**
 * Fetch past events
 */
export function usePastEvents() {
  return useQuery({
    queryKey: eventKeys.past(),
    queryFn: eventsService.getPastEvents,
  });
}

/**
 * Fetch upcoming events the user has RSVP'd "yes" to
 */
export function useGoingEvents(userId: string | null) {
  return useQuery({
    queryKey: eventKeys.going(userId || ''),
    queryFn: () => eventsService.getGoingEvents(userId || ''),
    enabled: !!userId,
  });
}

/**
 * Fetch upcoming events with pagination (infinite query)
 */
export function useInfiniteUpcomingEvents() {
  return useInfiniteQuery({
    queryKey: eventKeys.upcomingInfinite(),
    queryFn: ({ pageParam }) =>
      eventsService.getUpcomingEventsPaginated(pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

/**
 * Fetch past events with pagination (infinite query)
 */
export function useInfinitePastEvents() {
  return useInfiniteQuery({
    queryKey: eventKeys.pastInfinite(),
    queryFn: ({ pageParam }) =>
      eventsService.getPastEventsPaginated(pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

/**
 * Fetch upcoming events the user has RSVP'd "yes" to with pagination (infinite query)
 */
export function useInfiniteGoingEvents(userId: string | null) {
  return useInfiniteQuery({
    queryKey: eventKeys.goingInfinite(userId || ''),
    queryFn: ({ pageParam }) =>
      eventsService.getGoingEventsPaginated(userId || '', pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!userId,
  });
}

/**
 * Search events with debouncing
 * Only triggers search after 300ms pause and minimum 2 characters
 * Returns isPending: true when debouncing or fetching
 */
export function useSearchEvents(
  query: string,
  debounceMs = 300,
  minSearchLength = 2
) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query]);

  const isValidQuery = debouncedQuery.length >= minSearchLength;
  const willTriggerSearch = query.length >= minSearchLength;

  const queryResult = useQuery({
    queryKey: eventKeys.search(debouncedQuery),
    queryFn: () => eventsService.searchEvents(debouncedQuery),
    enabled: isValidQuery,
  });

  // Show loading when: debouncing with valid query OR actually fetching
  const isDebouncing = willTriggerSearch && query !== debouncedQuery;
  const isPending = isDebouncing || queryResult.isLoading;

  return {
    ...queryResult,
    isPending,
  };
}

/**
 * Fetch a single event by ID
 */
export function useEvent(id: number) {
  return useQuery({
    queryKey: eventKeys.detail(id),
    queryFn: () => eventsService.getEventById(id),
    enabled: !!id,
  });
}

/**
 * Fetch all events in a series by series_id
 */
export function useEventsBySeries(seriesId: string | null) {
  return useQuery({
    queryKey: eventKeys.series(seriesId || ''),
    queryFn: () => eventsService.getEventsBySeries(seriesId!),
    enabled: !!seriesId,
  });
}

/**
 * Create a new event
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: eventsService.createEvent,
    onSuccess: () => {
      // Invalidate all event-related queries
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

/**
 * Create a series of recurring events
 */
export function useCreateEventSeries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      baseEvent,
      recurrenceType,
      recurrenceCount,
      createdBy,
    }: {
      baseEvent: Parameters<typeof eventsService.createEvent>[0];
      recurrenceType: RecurrenceType;
      recurrenceCount: number;
      createdBy: string;
    }) =>
      eventsService.createEventSeries(
        baseEvent,
        recurrenceType,
        recurrenceCount,
        createdBy
      ),
    onSuccess: () => {
      // Invalidate all event-related queries
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

/**
 * Update an existing event
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: EventUpdate }) =>
      eventsService.updateEvent(id, updates),
    onSuccess: (data) => {
      // Update the specific event in cache
      queryClient.setQueryData(eventKeys.detail(data.id), data);
      // Invalidate all event-related queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

/**
 * Delete an event
 */
export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: eventsService.deleteEvent,
    onSuccess: (deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: eventKeys.detail(deletedId) });
      // Invalidate all event-related queries
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

/**
 * Fetch all events for export matching date filter (Admin only)
 * Note: enabled: false prevents auto-fetching - only fetches when refetch() is called
 */
export function useEventsForExport(options?: {
  dateFrom?: string;
  dateTo?: string;
}) {
  return useQuery({
    queryKey: eventKeys.export({
      dateFrom: options?.dateFrom,
      dateTo: options?.dateTo,
    }),
    queryFn: () => eventsService.getAllEventsForExport(options),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: false, // Only fetch when refetch() is explicitly called (on export button click)
  });
}
